import { useId, type ReactNode } from "react";
import { Check, Clock, Globe, ListChecks, Users } from "lucide-react";
import froppioBusiness from "@/assets/froppio-business.svg";
import { Card } from "@/shared/components/ui/Card";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { IconBadge } from "@/shared/components/ui/IconBadge";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import {
  useDashboardSetup,
  type DashboardSetupScope,
  type DashboardSetupStep,
  type DashboardSetupStepId,
} from "../hooks/useDashboardSetup";

const stepContent = {
  hours: { label: "Working hours", action: "Set up working hours", description: "Choose when your office is open.", icon: Clock },
  services: { label: "Services", action: "Add services", description: "What you offer, how long, how much.", icon: ListChecks },
  customers: { label: "Customers", action: "Add customers", description: "Bring your first familiar faces along.", icon: Users },
  website: { label: "Website", action: "Set up your website", description: "Give customers a place to book online.", icon: Globe },
} satisfies Record<DashboardSetupStepId, { label: string; action: string; description: string; icon: typeof Clock }>;

function SetupChecklist({ steps, completed }: { steps: DashboardSetupStep[]; completed: boolean }) {
  if (!steps.length) return null;

  return (
    <ul aria-label={completed ? "Completed setup actions" : "Upcoming setup actions"} className="grid gap-[7px]">
      {steps.map((step) => (
        <li key={step.id} className="flex min-h-10 items-center gap-2 rounded-xl border border-border bg-secondary px-3 py-[9px] text-[13px] leading-5 text-muted-foreground">
          {completed && <><Check className="size-4 shrink-0 text-primary" aria-hidden="true" /><span className="sr-only">Completed: </span></>}
          <span>{completed ? stepContent[step.id].label : stepContent[step.id].action}</span>
        </li>
      ))}
    </ul>
  );
}

interface DashboardBusinessSetupCardProps extends DashboardSetupScope {
  fallback: ReactNode;
}

export function DashboardBusinessSetupCard({ fallback, ...scope }: DashboardBusinessSetupCardProps) {
  const { steps, error, retry } = useDashboardSetup(scope);
  const { goToAvailabilities, goToServiceAdd, goToClientAdd, goToWebsite } = useAppNavigation();
  const headingId = useId();

  if (error) {
    return <ErrorState title="Couldn't check your business setup" message={error.message} onRetry={retry} />;
  }
  if (!steps) {
    return <Card tone="matcha"><LoadingSpinner message="Checking your business setup..." /></Card>;
  }

  const pending = steps.filter((step) => !step.complete);
  const [current, ...upcoming] = pending;
  if (!current) return fallback;

  const completed = steps.filter((step) => step.complete);
  const action = stepContent[current.id];
  const Icon = action.icon;
  const navigation = {
    hours: goToAvailabilities,
    services: goToServiceAdd,
    customers: goToClientAdd,
    website: goToWebsite,
  };

  return (
    <Card tone="matcha" role="region" aria-labelledby={headingId} className="border-0 p-[18px] sm:p-[22px]">
      <div className="mb-4 grid grid-cols-[minmax(0,1fr)_76px] items-center gap-2.5 sm:grid-cols-[minmax(0,1fr)_88px]">
        <div className="min-w-0">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.6px] text-primary">Welcome to Froppio</p>
          <h2 id={headingId} className="text-[23px] font-bold leading-tight tracking-[-0.5px] [text-wrap:balance]">Let's finish setting up your business.</h2>
        </div>
        <img src={froppioBusiness} alt="" width={280} height={240} className="h-auto w-full" />
      </div>
      <div className="grid gap-[7px] sm:max-w-[560px]">
        <SetupChecklist steps={completed} completed />
        <InfoRow
          title={action.action}
          caption={action.description}
          bold
          icon={<IconBadge tone="primary"><Icon aria-hidden="true" /></IconBadge>}
          onClick={navigation[current.id]}
          className="border-transparent"
        />
        <SetupChecklist steps={upcoming} completed={false} />
      </div>
    </Card>
  );
}
